import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendCampaign, sendCustomerCampaign, getUsage, getRecipients, getAudiences, getAudienceRecipients } from '../services/marketingApi';
import { api } from '../../../shared/services/api/client';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { TierLimitWarningModal } from '../../../shared/components/TierLimitWarningModal';
import { createLogger } from '../../../shared/config/logger';
import EmailCampaignPreview from '../components/EmailCampaignPreview';
import { LOYALPASS_PRIMARY_HEX } from '../../../shared/constants/loyalPassBrand';

const logger = createLogger('MarketingPage');

export default function Marketing() {
    const navigate = useNavigate();
    const [type, setType] = useState('email'); // 'email' or 'sms'
    const [title, setTitle] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [feedback, setFeedback] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [loading, setLoading] = useState(false);
    const [storeName, setStoreName] = useState('Your Store');
    const [storeColor, setStoreColor] = useState(LOYALPASS_PRIMARY_HEX);
    const [storeWebsite, setStoreWebsite] = useState('');
    const [storeLogoUrl, setStoreLogoUrl] = useState('');
    const [socialLinks, setSocialLinks] = useState({
        facebook_url: null,
        instagram_url: null,
        twitter_url: null,
        linkedin_url: null,
    });
    const [usage, setUsage] = useState({ tier: 'free', emails: 0, sms: 0, emailLimit: 25, smsLimit: 0 });
    const [loadingUsage, setLoadingUsage] = useState(true);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [recipientsList, setRecipientsList] = useState([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [selectedEmails, setSelectedEmails] = useState(new Set());
    const [audiences, setAudiences] = useState([]);
    const [audienceKey, setAudienceKey] = useState('');
    const [audienceRecipients, setAudienceRecipients] = useState([]);
    const [loadingAudienceRecipients, setLoadingAudienceRecipients] = useState(false);

    useEffect(() => {
        const fetchStoreName = async () => {
            try {
                const res = await api.get('/settings/general');
                const data = res.data ?? res;
                if (data.name) { setStoreName(data.name); }
                if (data.brand_color) { setStoreColor(data.brand_color); }
                if (data.website) {
                    const site = data.website.startsWith('http')
                        ? data.website
                        : `https://${data.website}`;
                    setStoreWebsite(site);
                } else {
                    setStoreWebsite('');
                }
                if (data.logo_url) {
                    setStoreLogoUrl(data.logo_url);
                }
                setSocialLinks({
                    facebook_url: data.facebook_url || null,
                    instagram_url: data.instagram_url || null,
                    twitter_url: data.twitter_url || null,
                    linkedin_url: data.linkedin_url || null,
                });
            } catch {
                // Use defaults if API fails
            }
        };

        const fetchUsage = async () => {
            try {
                setLoadingUsage(true);
                const response = await getUsage();
                setUsage(response.data);
            } catch (error) {
                logger.error('Failed to fetch usage stats', error);
            } finally {
                setLoadingUsage(false);
            }
        };

        fetchStoreName();
        fetchUsage();
    }, []);

    // Fetch customers for recipient selection (opt-in only shown)
    useEffect(() => {
        const fetchRecipients = async () => {
            try {
                setLoadingRecipients(true);
                const res = await getRecipients();
                const raw = res.data?.data || res.data || [];
                setRecipientsList(Array.isArray(raw) ? raw : []);
            } catch (err) {
                logger.error('Failed to fetch recipients', err);
                setRecipientsList([]);
            } finally {
                setLoadingRecipients(false);
            }
        };
        fetchRecipients();
    }, []);

    // Fetch available audiences
    useEffect(() => {
        const fetchAudiences = async () => {
            try {
                const res = await getAudiences();
                const list = res.data?.audiences || [];
                setAudiences(Array.isArray(list) ? list : []);
            } catch (err) {
                logger.error('Failed to fetch audiences', err);
                setAudiences([]);
            }
        };
        fetchAudiences();
    }, []);

    // When audience is selected, fetch its recipients
    useEffect(() => {
        if (!audienceKey) {
            setAudienceRecipients([]);
            return;
        }
        let cancelled = false;
        const fetch = async () => {
            setLoadingAudienceRecipients(true);
            try {
                const res = await getAudienceRecipients(audienceKey);
                if (!cancelled) {
                    setAudienceRecipients(res.data?.recipients || []);
                }
            } catch (err) {
                if (!cancelled) {
                    setAudienceRecipients([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingAudienceRecipients(false);
                }
            }
        };
        fetch();
        return () => { cancelled = true; };
    }, [audienceKey]);

    // Opt-in customers only, deduped by email (one row per customer)
    const optInRecipients = useMemo(() => {
        const seen = new Set();
        return (recipientsList || [])
            .filter((r) => r.marketing_opt_in === true && r.customer_email)
            .filter((r) => {
                const email = (r.customer_email || '').trim().toLowerCase();
                if (seen.has(email)) {
                    return false;
                }
                seen.add(email);
                return true;
            })
            .map((r) => ({
                email: (r.customer_email || '').trim().toLowerCase(),
                name: r.customer_name || r.customer_email || '',
            }));
    }, [recipientsList]);

    const handleSubmit = async () => {
        if (!title || !message || (type === 'email' && !subject)) {
            setFeedback('⚠️ Please fill in all required fields.');
            return;
        }

        setLoading(true);
        setFeedback('');

        try {
            await sendCampaign(type, title, subject, message, storeLogoUrl);
            setFeedback('✅ Campaign sent successfully!');
            setTitle('');
            setSubject('');
            setMessage('');
            setShowPreview(false);
            const response = await getUsage();
            setUsage(response.data);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            if (errorMessage.includes('limit exceeded') || errorMessage.includes('Please upgrade')) {
                setWarningMessage(errorMessage);
                setShowWarningModal(true);
                setFeedback('');
            } else {
                setFeedback(`❌ ${errorMessage}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSendToSelected = async (emailsToSend) => {
        if (!title || !message || (type === 'email' && !subject)) {
            setFeedback('⚠️ Please fill in all required fields.');
            return;
        }
        const emails = Array.isArray(emailsToSend) ? emailsToSend : Array.from(selectedEmails);
        if (emails.length === 0) {
            setFeedback('⚠️ Select at least one recipient or an audience.');
            return;
        }

        setLoading(true);
        setFeedback('');

        try {
            const res = await sendCustomerCampaign(emails, subject, title, message, storeLogoUrl);
            const sent = res.data?.sent ?? emails.length;
            setFeedback(`✅ Campaign sent to ${sent} recipient(s).`);
            setTitle('');
            setSubject('');
            setMessage('');
            setShowPreview(false);
            setSelectedEmails(new Set());
            setAudienceKey('');
            setAudienceRecipients([]);
            const response = await getUsage();
            setUsage(response.data);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            if (errorMessage.includes('limit exceeded') || errorMessage.includes('Please upgrade')) {
                setWarningMessage(errorMessage);
                setShowWarningModal(true);
                setFeedback('');
            } else {
                setFeedback(`❌ ${errorMessage}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (type !== 'email') {
            await handleSubmit();
            return;
        }
        if (audienceKey && audienceRecipients.length > 0) {
            await handleSendToSelected(audienceRecipients.map((r) => r.email));
            return;
        }
        if (selectedEmails.size > 0) {
            await handleSendToSelected();
            return;
        }
        await handleSubmit();
    };

    const sendButtonLabel = (() => {
        if (loading) {
            return 'Sending...';
        }
        if (type !== 'email') {
            return 'Send Campaign';
        }
        if (audienceKey && audienceRecipients.length > 0) {
            const name = audiences.find((a) => a.id === audienceKey)?.name || audienceKey;
            return `Send to ${name} (${audienceRecipients.length})`;
        }
        if (selectedEmails.size > 0) {
            return `Send to selected (${selectedEmails.size})`;
        }
        return 'Send to all opt-in';
    })();

    const toggleRecipient = (email) => {
        setSelectedEmails((prev) => {
            const next = new Set(prev);
            if (next.has(email)) {
                next.delete(email);
            } else {
                next.add(email);
            }
            return next;
        });
    };

    const selectAllRecipients = () => {
        setSelectedEmails(new Set(optInRecipients.map((r) => r.email)));
    };

    const deselectAllRecipients = () => {
        setSelectedEmails(new Set());
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-primary mb-6">Marketing Campaigns</h2>

            {/* Metric Cards - 2 cols on mobile so cards share space */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
                {/* Email Metric Card */}
                <div className="bg-white shadow-md rounded-xl p-3 sm:p-6">
                    <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 line-clamp-2">Emails Sent (This Month)</p>
                        {loadingUsage ? (
                            <p className="text-lg sm:text-2xl font-bold text-gray-400 mt-1 sm:mt-2">Loading...</p>
                        ) : (
                            <p className="text-lg sm:text-2xl font-bold text-primary mt-1 sm:mt-2">
                                {usage.emails}
                                {usage.emailLimit !== null && (
                                    <span className="text-sm sm:text-lg font-normal text-gray-500"> / {usage.emailLimit}</span>
                                )}
                            </p>
                        )}
                    </div>
                </div>

                {/* SMS Metric Card - Only show if not free tier */}
                {usage.tier !== 'free' && (
                    <div className="bg-white shadow-md rounded-xl p-3 sm:p-6">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600 line-clamp-2">SMS Sent (This Month)</p>
                            {loadingUsage ? (
                                <p className="text-lg sm:text-2xl font-bold text-gray-400 mt-1 sm:mt-2">Loading...</p>
                            ) : (
                                <p className="text-lg sm:text-2xl font-bold text-primary mt-1 sm:mt-2">
                                    {usage.sms}
                                    {usage.smsLimit !== null && (
                                        <span className="text-sm sm:text-lg font-normal text-gray-500"> / {usage.smsLimit}</span>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* 📋 Form */}
                <div className="flex-1 bg-white shadow-md rounded-xl p-6 space-y-4">
                    <div className="flex gap-4">
                        <label className="font-medium">Campaign Type:</label>
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="border rounded p-2"
                        >
                            <option value="email">Email</option>
                            <option value="sms" disabled={true}>
                                SMS
                            </option>
                        </select>
                    </div>

                    {type === 'email' && (
                        <div>
                            <label className="block font-medium">Email's Subject:</label>
                            <input
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                className="w-full border rounded p-2"
                                placeholder="Subject line for email"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block font-medium">Title:</label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full border rounded p-2"
                            placeholder="Campaign title"
                        />
                    </div>

                    <div>
                        <label className="block font-medium">Message:</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={6}
                            className="w-full border rounded p-2"
                            placeholder={type === 'email' ? 'Email body...' : 'SMS message...'}
                        />
                    </div>

                    <div>
                        <label className="block font-medium">Logo URL:</label>
                        <input
                            value={storeLogoUrl}
                            onChange={(e) => setStoreLogoUrl(e.target.value)}
                            className="w-full border rounded p-2"
                            placeholder="https://example.com/logo.png"
                        />
                    </div>

                    <div className="flex flex-wrap gap-4 mt-4">
                        <button
                            onClick={() => setShowPreview(true)}
                            className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
                        >
                            Preview
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={
                                loading ||
                                (type === 'email' && optInRecipients.length === 0 && !audienceKey) ||
                                (type === 'email' && audienceKey && audienceRecipients.length === 0 && !loadingAudienceRecipients)
                            }
                            className={`px-6 py-2 rounded text-white ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'}`}
                        >
                            {sendButtonLabel}
                        </button>
                    </div>

                    {feedback && (
                        <div className="mt-4">
                            {feedback.startsWith('❌') ? (
                                <div>
                                    <p className="text-sm text-red-600 mb-2">{feedback}</p>
                                    {(feedback.includes('limit exceeded') || feedback.includes('Please upgrade')) && (
                                        <button
                                            type="button"
                                            onClick={() => navigate('/dashboard/settings/membership')}
                                            className="mt-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition text-sm font-medium"
                                        >
                                            Upgrade Plan
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-green-600">{feedback}</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Right: Audience card (email only) */}
                {type === 'email' && (
                    <div className="flex-1 bg-white shadow-md rounded-xl p-6 space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">Audience</h3>
                        <label className="block font-medium">Send to</label>
                        <select
                            value={audienceKey}
                            onChange={(e) => setAudienceKey(e.target.value)}
                            className="w-full border rounded p-2 bg-white"
                        >
                            <option value="">Manual selection (choose below)</option>
                            {audiences.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.name} – {a.description}
                                </option>
                            ))}
                        </select>
                        {audienceKey ? (
                            <>
                                <p className="text-sm text-gray-600">
                                    {loadingAudienceRecipients
                                        ? 'Loading…'
                                        : `${audienceRecipients.length} recipient(s) in this audience`}
                                </p>
                                {!loadingAudienceRecipients && audienceRecipients.length > 0 && (
                                    <div className="max-h-64 overflow-y-auto border rounded p-2 bg-gray-50 space-y-1">
                                        {audienceRecipients.map((r) => (
                                            <div
                                                key={r.email}
                                                className="flex items-center gap-2 p-1 rounded text-sm"
                                                title={r.email}
                                            >
                                                <span className="truncate flex-1">{r.name || r.email}</span>
                                                <span className="text-xs text-gray-400 truncate max-w-[140px]" title={r.email}>
                                                    {r.email}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <label className="block font-medium">Recipients</label>
                                {loadingRecipients ? (
                                    <p className="text-sm text-gray-500">Loading recipients...</p>
                                ) : optInRecipients.length === 0 ? (
                                    <p className="text-sm text-gray-500">No customers have opted in to marketing emails yet.</p>
                                ) : (
                                    <>
                                        <div className="flex gap-2 mb-2">
                                            <button
                                                type="button"
                                                onClick={selectAllRecipients}
                                                className="text-sm text-primary hover:underline"
                                            >
                                                Select all
                                            </button>
                                            <span className="text-gray-400">|</span>
                                            <button
                                                type="button"
                                                onClick={deselectAllRecipients}
                                                className="text-sm text-gray-600 hover:underline"
                                            >
                                                Deselect all
                                            </button>
                                            <span className="text-sm text-gray-500">
                                                ({selectedEmails.size} of {optInRecipients.length} selected)
                                            </span>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto border rounded p-2 bg-gray-50 space-y-1">
                                            {optInRecipients.map((r) => (
                                                <label key={r.email} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-1 rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedEmails.has(r.email)}
                                                        onChange={() => toggleRecipient(r.email)}
                                                        className="rounded border-gray-300"
                                                    />
                                                    <span className="text-sm truncate" title={r.email}>
                                                        {r.name || r.email}
                                                    </span>
                                                    <span className="text-xs text-gray-400 truncate flex-1" title={r.email}>
                                                        {r.email}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Preview - below both cards */}
            {showPreview && (
                <div className="mt-8 w-full bg-white shadow-md rounded-xl p-6 overflow-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {type === 'email' ? '📬 Email Preview' : '📱 SMS Preview'}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowPreview(false)}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                            >
                                Close Preview
                            </button>
                        </div>
                        {type === 'email' ? (
                            <EmailCampaignPreview
                                storeName={storeName}
                                storeColor={storeColor}
                                storeLogoUrl={storeLogoUrl}
                                storeWebsite={storeWebsite}
                                socialSettings={socialLinks}
                                title={title}
                                message={message}
                            />
                        ) : (
                            <div className="border bg-gray-100 rounded-lg p-4 text-sm font-mono text-gray-800 shadow-inner h-fit">
                                <div className="text-xs text-gray-400 mb-1">From: Your Store</div>
                                <div className="whitespace-pre-wrap leading-relaxed">
                                    {message || 'Thanks for visiting! Enjoy 20% off your next order. 🍔'}
                                </div>
                            </div>
                        )}
                </div>
            )}
            <TierLimitWarningModal
                isOpen={showWarningModal}
                onClose={() => setShowWarningModal(false)}
                message={warningMessage}
            />
        </div>
    );
}

